/**
 * PB-Profil — Form-Controls
 * Senihelp24 Brand: Magenta #b5297a · Outfit · 8/16-Radius
 *
 * All controls are dark-mode aware via the `dark` prop; visuals match the
 * Claude-Design HTML prototype 1:1 (see senihelp24-design-system/project/pb-profil).
 */
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../../lib/theme';
import { useIsMobile } from '../../../lib/useBreakpoint';
import { DateInput } from '../../../components/DateInput';
import { useOptionLabel } from '../../../lib/useOptionLabel';

/* ─── Section card ─────────────────────────────────────────── */
/**
 * Optionaler Schalter im Sektionskopf: Sektion bei Bedarf aktivieren. Aus =
 * Inhalt eingeklappt, nur der Hinweis `offHint` bleibt sichtbar.
 */
export interface SectionToggle {
  value: boolean;
  onChange: (v: boolean) => void;
  /** Accessible Name des Schalters. */
  label: string;
  offHint: string;
}
interface SectionProps {
  id: string;
  num: string;
  title: string;
  desc?: string;
  children: ReactNode;
  dark?: boolean;
  toggle?: SectionToggle;
}
export function Section({ id, num, title, desc, children, dark = false, toggle }: SectionProps) {
  const t = getThemeColors(dark);
  const collapsed = toggle != null && !toggle.value;
  return (
    <section
      id={id}
      data-section={id}
      style={{
        background: t.card,
        border: `1px solid ${t.border}`,
        borderRadius: 16,
        boxShadow: dark ? 'none' : '0 1px 2px rgba(16,24,40,0.05)',
        scrollMarginTop: 24,
        overflow: 'hidden',
      }}
    >
      <div style={{
        padding: '18px 24px', borderBottom: collapsed ? 'none' : `1px solid ${t.borderSoft}`,
        display: 'flex', alignItems: 'baseline', gap: 12,
      }}>
        <span style={{
          fontSize: 12, fontWeight: 700, color: BRAND, background: BRAND_LIGHT,
          width: 26, height: 26, borderRadius: 8, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'center',
          opacity: collapsed ? 0.6 : 1,
        }}>{num}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: collapsed ? t.muted : t.heading, lineHeight: 1.3 }}>{title}</h3>
          {desc && <div style={{ fontSize: 12.5, color: t.muted, marginTop: 2 }}>{collapsed ? toggle.offHint : desc}</div>}
        </div>
        {toggle && (
          <Switch dark={dark} value={toggle.value} onChange={toggle.onChange} ariaLabel={toggle.label} />
        )}
      </div>
      {!collapsed && <div style={{ padding: '22px 24px' }}>{children}</div>}
    </section>
  );
}

/** Kompakter An/Aus-Schalter (gleiche Optik wie in ToggleRow). */
export function Switch({ value, onChange, ariaLabel, dark = false }: {
  value: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  dark?: boolean;
}) {
  const t = getThemeColors(dark);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel}
      onClick={() => onChange(!value)}
      style={{
        width: 40, height: 22, borderRadius: 999, flexShrink: 0, position: 'relative',
        background: value ? BRAND : t.border, transition: 'background 0.15s',
        border: 'none', padding: 0, cursor: 'pointer', alignSelf: 'center',
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: value ? 20 : 2, width: 18, height: 18,
        borderRadius: '50%', background: '#fff', transition: 'left 0.15s',
        boxShadow: '0 1px 2px rgba(16,24,40,0.2)',
      }} />
    </button>
  );
}

/* ─── Sub-group heading inside a section ───────────────────── */
export function GroupLabel({ children, top, dark = false }: { children: ReactNode; top?: boolean; dark?: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase',
      letterSpacing: '0.06em', marginTop: top ? 0 : 22, marginBottom: 14,
      paddingBottom: 8, borderBottom: `1px solid ${t.borderSoft}`,
    }}>{children}</div>
  );
}

/* ─── Responsive grid ──────────────────────────────────────── */
// Form-section grid. Default 2 columns on desktop, single column on mobile
// (≤768px). Sections that pass `cols={3}` collapse to 1 on mobile too;
// horizontally-cramped fields are unusable on a 390px viewport.
export function Grid({ cols = 2, children }: { cols?: number; children: ReactNode }) {
  const isMobile = useIsMobile();
  const effective = isMobile ? 1 : cols;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${effective}, minmax(0, 1fr))`,
      gap: isMobile ? '12px 0' : '16px 20px',
    }}>{children}</div>
  );
}

/* ─── Field wrapper ────────────────────────────────────────── */
interface FieldProps {
  label?: string;
  required?: boolean;
  hint?: string;
  span?: number;
  children: ReactNode;
  dark?: boolean;
  /** WP4: highlight the field as invalid (red outline + label). */
  hasError?: boolean;
}
export function Field({ label, required, hint, span, children, dark = false, hasError }: FieldProps) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const isMobile = useIsMobile();
  // On mobile the wrapping Grid collapses to a single column. Honouring
  // `span` here would force an implicit second column and break the
  // single-column stack — so we drop the span on small viewports.
  const effectiveSpan = isMobile ? undefined : span;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 6,
      gridColumn: effectiveSpan ? `span ${effectiveSpan}` : undefined, minWidth: 0,
      outline: hasError ? `1px solid ${ERROR_BORDER}` : undefined,
      borderRadius: hasError ? 8 : undefined,
      padding: hasError ? '4px 6px 4px 6px' : undefined,
    }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 500, color: hasError ? '#b42318' : t.text, display: 'flex', gap: 4 }}>
          {label}{required && <span style={{ color: BRAND }}>*</span>}
        </label>
      )}
      {children}
      {hasError && (
        <span style={{ fontSize: 11, color: '#b42318', lineHeight: 1.3 }}>{tr('common.field.required_error')}</span>
      )}
      {hint && <span style={{ fontSize: 11.5, color: t.muted, lineHeight: 1.4 }}>{hint}</span>}
    </div>
  );
}

/* ─── Shared input styles ──────────────────────────────────── */
function inputStyle(dark: boolean, readOnly = false): React.CSSProperties {
  const t = getThemeColors(dark);
  return {
    fontFamily: 'Outfit, sans-serif',
    fontSize: 14,
    lineHeight: '20px',
    border: `1px solid ${t.border}`,
    borderRadius: 8,
    padding: '9px 12px',
    color: readOnly ? t.muted : t.heading,
    background: readOnly ? t.cardMuted : t.inputBg,
    outline: 'none',
    width: '100%',
    transition: 'border-color 0.12s, box-shadow 0.12s',
  };
}
function focusOn(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
  e.currentTarget.style.borderColor = '#ec6db8';
  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(181,41,122,0.12)';
}
/** Rahmenfarbe eines nicht ausgefüllten Pflichtfeldes. */
export const ERROR_BORDER = '#f04438';

/**
 * `hasError` muss hier durchgereicht werden: sonst setzt der Blur die Border
 * auf die neutrale Default-Farbe zurück und die rote Pflichtfeld-Markierung
 * verschwindet, sobald der Nutzer das Feld einmal angeklickt hat.
 */
function focusOff(dark: boolean, hasError?: boolean) {
  return (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const t = getThemeColors(dark);
    e.currentTarget.style.borderColor = hasError ? ERROR_BORDER : t.border;
    e.currentTarget.style.boxShadow = 'none';
  };
}

/* ─── Text / number / date input ───────────────────────────── */
interface TextProps {
  value: string | number | undefined | null;
  onChange?: (v: string) => void;
  placeholder?: string;
  type?: string;
  prefix?: string;
  suffix?: string;
  readOnly?: boolean;
  dark?: boolean;
  /** Pflichtfeld nicht ausgefüllt — roter Rahmen (analog `Select`). */
  hasError?: boolean;
}
export function Text({ value, onChange, type = 'text', prefix, suffix, readOnly, dark = false, hasError }: TextProps) {
  const t = getThemeColors(dark);
  // Datumsfelder: deutsches Format „TT.MM.JJJJ" statt Browser-Locale; Wert bleibt „YYYY-MM-DD".
  if (type === 'date') {
    return (
      <DateInput
        value={value == null ? '' : String(value)}
        onChange={v => onChange && onChange(v)}
        readOnly={readOnly}
        style={{
          ...inputStyle(dark, readOnly),
          cursor: readOnly ? 'default' : 'text',
          ...(hasError ? { borderColor: ERROR_BORDER } : {}),
        }}
      />
    );
  }
  const input = (
    <input
      type={type}
      value={value ?? ''}
      // Platzhalter bewusst nicht gerendert (Profilformulare ohne Beispielwerte).
      readOnly={readOnly}
      aria-invalid={hasError || undefined}
      onChange={e => onChange && onChange(e.target.value)}
      onFocus={focusOn}
      onBlur={focusOff(dark, hasError)}
      style={{
        ...inputStyle(dark, readOnly),
        paddingLeft: prefix ? 34 : 12,
        paddingRight: suffix ? 44 : 12,
        cursor: readOnly ? 'default' : 'text',
        // Roter Rahmen überschreibt die Default-Border aus inputStyle().
        ...(hasError ? { borderColor: ERROR_BORDER } : {}),
      }}
    />
  );
  if (!prefix && !suffix) return input;
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      {prefix && <span style={{ position: 'absolute', left: 12, fontSize: 13.5, color: t.muted, pointerEvents: 'none' }}>{prefix}</span>}
      {input}
      {suffix && <span style={{ position: 'absolute', right: 12, fontSize: 13, color: t.muted, pointerEvents: 'none' }}>{suffix}</span>}
    </div>
  );
}

/* ─── Textarea ─────────────────────────────────────────────── */
/**
 * Paket A (A6): Auto-Höhe. Die Textarea wächst mit dem Inhalt, so dass der
 * volle Text ohne inneres Scrollen sichtbar ist. `rows` bleibt die
 * Mindesthöhe; manuelles Ziehen (resize: vertical) ist weiterhin möglich.
 */
export function Area({ value, onChange, rows = 3, dark = false }: {
  value: string | undefined | null;
  onChange?: (v: string) => void;
  placeholder?: string;
  rows?: number;
  dark?: boolean;
}) {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Erst zurücksetzen, sonst schrumpft die Höhe beim Löschen nie.
    el.style.height = 'auto';
    const h = el.scrollHeight;
    if (h > 0) el.style.height = `${h}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={rows}
      value={value ?? ''}
      // Platzhalter bewusst nicht gerendert (Profilformulare ohne Beispielwerte).
      onChange={e => onChange && onChange(e.target.value)}
      onFocus={focusOn}
      onBlur={focusOff(dark)}
      style={{ ...inputStyle(dark), resize: 'vertical', overflow: 'hidden', lineHeight: 1.6, padding: '10px 12px', boxSizing: 'border-box' }}
    />
  );
}

/* ─── Reiner Text (Freitexte read-only) ────────────────────── */
/**
 * Paket A (A5): gibt einen Freitext nur als Text aus (kein textarea).
 * Zeilenumbrüche bleiben erhalten; leer → „—".
 */
export function PlainText({ value, dark = false }: { value: string | undefined | null; dark?: boolean }) {
  const t = getThemeColors(dark);
  const text = (value ?? '').trim();
  return (
    <div
      data-plain-text
      style={{
        fontFamily: 'Outfit, sans-serif', fontSize: 14, lineHeight: 1.6,
        color: text ? t.text : t.muted, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        padding: '4px 0',
      }}
    >{text || '—'}</div>
  );
}

/* ─── Telefon: Land (Flagge + Vorwahl) + Nummer ────────────── */
/**
 * Paket A (A10): Rufnummer als Land-Dropdown (Flagge + Vorwahl) plus
 * separate Nummer. Nach außen bleibt der Wert ein String im E.164-Format
 * (z.B. +491701234567), d.h. das bestehende Feld `telefon`/`mobile` wird
 * unverändert weitergenutzt. Keine neue Dependency.
 */
export interface PhoneCountry { iso: string; dial: string; name: string }

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { iso: 'DE', dial: '49',  name: 'Deutschland' },
  { iso: 'PL', dial: '48',  name: 'Polen' },
  { iso: 'AT', dial: '43',  name: 'Österreich' },
  { iso: 'CH', dial: '41',  name: 'Schweiz' },
  { iso: 'NL', dial: '31',  name: 'Niederlande' },
  { iso: 'BE', dial: '32',  name: 'Belgien' },
  { iso: 'LU', dial: '352', name: 'Luxemburg' },
  { iso: 'FR', dial: '33',  name: 'Frankreich' },
  { iso: 'IT', dial: '39',  name: 'Italien' },
  { iso: 'ES', dial: '34',  name: 'Spanien' },
  { iso: 'GB', dial: '44',  name: 'Großbritannien' },
  { iso: 'DK', dial: '45',  name: 'Dänemark' },
  { iso: 'CZ', dial: '420', name: 'Tschechien' },
  { iso: 'SK', dial: '421', name: 'Slowakei' },
  { iso: 'HU', dial: '36',  name: 'Ungarn' },
  { iso: 'RO', dial: '40',  name: 'Rumänien' },
  { iso: 'BG', dial: '359', name: 'Bulgarien' },
  { iso: 'HR', dial: '385', name: 'Kroatien' },
  { iso: 'LT', dial: '370', name: 'Litauen' },
  { iso: 'UA', dial: '380', name: 'Ukraine' },
  { iso: 'TR', dial: '90',  name: 'Türkei' },
];

/** ISO-3166-Alpha-2 → Flaggen-Emoji (Regional-Indicator-Symbole). */
export function flagEmoji(iso: string): string {
  return iso.toUpperCase().replace(/./g, ch => String.fromCodePoint(0x1f1e6 - 65 + ch.charCodeAt(0)));
}

/**
 * Zerlegt einen gespeicherten Wert in Land + Nummer.
 *  „+49 170 1234567" / „0049170…" → DE + „1701234567"
 *  „0170 1234567" (ohne Vorwahl)  → DE + „0170 1234567" (unverändert)
 *  leer                            → DE + „"
 * Längste passende Vorwahl gewinnt (z.B. 420 vor 42).
 */
export function parsePhone(value: string | null | undefined, defaultIso = 'DE'): { iso: string; number: string } {
  const raw = (value ?? '').trim();
  if (!raw) return { iso: defaultIso, number: '' };
  let intl: string | null = null;
  if (raw.startsWith('+')) intl = raw.slice(1);
  else if (raw.startsWith('00')) intl = raw.slice(2);
  if (intl == null) return { iso: defaultIso, number: raw };
  const digits = intl.replace(/[^\d]/g, '');
  const match = [...PHONE_COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find(c => digits.startsWith(c.dial));
  if (!match) return { iso: defaultIso, number: raw };
  return { iso: match.iso, number: digits.slice(match.dial.length) };
}

/** Setzt Land + Nummer wieder zu E.164 zusammen; leere Nummer → „". */
export function composePhone(iso: string, number: string): string {
  const c = PHONE_COUNTRIES.find(x => x.iso === iso) ?? PHONE_COUNTRIES[0];
  const digits = number.replace(/[^\d]/g, '').replace(/^0+/, '');
  return digits ? `+${c.dial}${digits}` : '';
}

export function PhoneInput({ value, onChange, dark = false, hasError, ariaLabel }: {
  value: string | undefined | null;
  onChange?: (v: string) => void;
  placeholder?: string;
  dark?: boolean;
  hasError?: boolean;
  ariaLabel?: string;
}) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  // Land als eigener State: der Nutzer wählt es, bevor eine Nummer da ist —
  // aus dem (leeren) Wert allein ließe es sich nicht rekonstruieren.
  const [iso, setIso] = React.useState(() => parsePhone(value).iso);
  const [number, setNumber] = React.useState(() => parsePhone(value).number);
  // Zuletzt selbst gemeldeter Wert: kommt von außen etwas anderes (Laden,
  // Reset), wird neu zerlegt; die eigene Eingabe wird nicht überschrieben.
  const lastEmitted = React.useRef<string>(value ?? '');
  React.useEffect(() => {
    if ((value ?? '') === lastEmitted.current) return;
    const p = parsePhone(value);
    lastEmitted.current = value ?? '';
    setIso(p.iso);
    setNumber(p.number);
  }, [value]);

  const emit = (nextIso: string, nextNumber: string) => {
    const v = composePhone(nextIso, nextNumber);
    lastEmitted.current = v;
    onChange && onChange(v);
  };
  const dial = PHONE_COUNTRIES.find(c => c.iso === iso)?.dial ?? '49';
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
      <select
        aria-label={ariaLabel ? `${ariaLabel}: ${tr('pflege.form.field.land')}` : tr('pflege.form.field.land')}
        value={iso}
        onChange={e => { setIso(e.target.value); emit(e.target.value, number); }}
        onFocus={focusOn}
        onBlur={focusOff(dark, hasError)}
        style={{
          ...inputStyle(dark), width: 'auto', flex: '0 0 auto', paddingRight: 8, cursor: 'pointer',
          ...(hasError ? { borderColor: ERROR_BORDER } : {}),
        }}
      >
        {PHONE_COUNTRIES.map(c => (
          <option key={c.iso} value={c.iso}>{`${flagEmoji(c.iso)} +${c.dial}`}</option>
        ))}
      </select>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <span style={{ position: 'absolute', left: 12, fontSize: 13.5, color: t.muted, pointerEvents: 'none' }}>+{dial}</span>
        <input
          type="tel"
          inputMode="tel"
          aria-label={ariaLabel}
          aria-invalid={hasError || undefined}
          value={number}
          // Platzhalter bewusst nicht gerendert (Profilformulare ohne Beispielwerte).
          onChange={e => { setNumber(e.target.value); emit(iso, e.target.value); }}
          onFocus={focusOn}
          onBlur={focusOff(dark, hasError)}
          style={{
            ...inputStyle(dark),
            paddingLeft: 12 + 10 + dial.length * 8,
            ...(hasError ? { borderColor: ERROR_BORDER } : {}),
          }}
        />
      </div>
    </div>
  );
}

/* ─── Select dropdown ──────────────────────────────────────── */
export interface SelectOption {
  value: string;
  label: string;
}
export function Select({
  value, onChange, options, placeholder, disabled, dark = false, hasError,
}: {
  value: string | number | undefined | null;
  onChange?: (v: string) => void;
  options: Array<SelectOption | string>;
  placeholder?: string;
  disabled?: boolean;
  dark?: boolean;
  /** WP4: marks the native <select> as aria-invalid. */
  hasError?: boolean;
}) {
  const t = getThemeColors(dark);
  const optLabel = useOptionLabel();
  return (
    <div style={{ position: 'relative', opacity: disabled ? 0.55 : 1 }}>
      <select
        value={value == null ? '' : String(value)}
        disabled={disabled}
        aria-invalid={hasError || undefined}
        onChange={e => onChange && onChange(e.target.value)}
        onFocus={focusOn}
        onBlur={focusOff(dark, hasError)}
        style={{
          ...inputStyle(dark),
          appearance: 'none',
          WebkitAppearance: 'none',
          paddingRight: 34,
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: value === '' || value == null ? t.placeholder : t.heading,
          background: disabled ? t.cardMuted : t.inputBg,
          ...(hasError ? { borderColor: ERROR_BORDER } : {}),
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => {
          const val = typeof o === 'string' ? o : o.value;
          const lbl = optLabel(typeof o === 'string' ? o : o.label);
          return <option key={val} value={val} style={{ color: t.heading, background: t.card }}>{lbl}</option>;
        })}
      </select>
      <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        width="16" height="16" viewBox="0 0 20 20" fill="none" stroke={t.muted} strokeWidth="1.5" strokeLinecap="round">
        <path d="M4.8 7.4L10 12.6l5.2-5.2" />
      </svg>
    </div>
  );
}

/* ─── Range slider (0–10 etc.) ─────────────────────────────── */
export function Range({
  value, onChange, min = 0, max = 10, step = 0.5, suffix, dark = false,
}: {
  value: string | number | undefined | null;
  onChange?: (v: string) => void;
  min?: number; max?: number; step?: number; suffix?: string; dark?: boolean;
}) {
  const t = getThemeColors(dark);
  const v = value === '' || value == null ? min : Number(value);
  const pct = ((v - min) / (max - min)) * 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <input
        type="range" min={min} max={max} step={step} value={v}
        onChange={e => onChange && onChange(e.target.value)}
        style={{
          flex: 1, WebkitAppearance: 'none', appearance: 'none',
          height: 6, borderRadius: 9999, outline: 'none', cursor: 'pointer',
          background: `linear-gradient(to right, ${BRAND} 0%, ${BRAND} ${pct}%, ${t.border} ${pct}%, ${t.border} 100%)`,
        }}
        className="sh-range"
      />
      <span style={{
        minWidth: 56, textAlign: 'center', fontSize: 13, fontWeight: 600,
        color: BRAND_DARK, background: BRAND_LIGHT, borderRadius: 8, padding: '5px 8px',
      }}>
        {v}{suffix ? ` ${suffix}` : ''}
      </span>
    </div>
  );
}

/* ─── Read-only system field ───────────────────────────────── */
export function ReadOnly({ value, dark = false }: { value: string | undefined | null; dark?: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{
      ...inputStyle(dark, true),
      display: 'flex', alignItems: 'center', gap: 8, cursor: 'default',
    }}>
      <svg width="13" height="13" viewBox="0 0 20 20" fill={t.muted}>
        <path fillRule="evenodd" d="M10 1.5a4.25 4.25 0 0 0-4.25 4.25V8H5.5A1.75 1.75 0 0 0 3.75 9.75v6.5A1.75 1.75 0 0 0 5.5 18h9a1.75 1.75 0 0 0 1.75-1.75v-6.5A1.75 1.75 0 0 0 14.5 8h-.25V5.75A4.25 4.25 0 0 0 10 1.5zM12.75 8h-5.5V5.75a2.75 2.75 0 0 1 5.5 0V8z" clipRule="evenodd" />
      </svg>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '—'}</span>
    </div>
  );
}

/* ─── Star rating (read-only) ──────────────────────────────── */
export function Rating({ value = 0, dark = false }: { value?: number; dark?: boolean }) {
  const t = getThemeColors(dark);
  const v = Number(value) || 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ display: 'flex', gap: 2 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <svg key={i} width="18" height="18" viewBox="0 0 20 20" fill={i <= Math.round(v) ? '#f79009' : t.border}>
            <path d="M10 1.6l2.47 5.0 5.53.8-4 3.9.94 5.5L10 14.7l-4.94 2.6.94-5.5-4-3.9 5.53-.8z" />
          </svg>
        ))}
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{v.toFixed(1)}</span>
    </div>
  );
}

/* ─── Checkbox group (multi-select chips) ──────────────────── */
export function CheckGroup({
  value = [], onChange, options, cols = 2, dark = false,
}: {
  value?: string[];
  onChange?: (v: string[]) => void;
  options: Array<SelectOption | string>;
  cols?: number;
  dark?: boolean;
}) {
  const t = getThemeColors(dark);
  const isMobile = useIsMobile();
  const optLabel = useOptionLabel();
  const toggle = (val: string) => {
    const set = new Set(value);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    onChange && onChange([...set]);
  };
  const effectiveCols = isMobile ? 1 : cols;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${effectiveCols}, minmax(0,1fr))`, gap: 8 }}>
      {options.map(o => {
        const val = typeof o === 'string' ? o : o.value;
        const lbl = optLabel(typeof o === 'string' ? o : o.label);
        const on = value.includes(val);
        return (
          <label key={val} style={{
            display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
            border: `1px solid ${on ? BRAND : t.border}`, borderRadius: 9,
            padding: '9px 12px', background: on ? (dark ? '#2a1222' : BRAND_LIGHT) : t.card,
            fontSize: 13, fontWeight: 500, color: on ? (dark ? '#f49dd1' : BRAND_DARK) : t.text,
            transition: 'all 0.12s', minWidth: 0,
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: 5, flexShrink: 0,
              border: `1.5px solid ${on ? BRAND : t.border}`,
              background: on ? BRAND : t.inputBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {on && (
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.5 6.2l2.2 2.2 4.8-4.8" />
                </svg>
              )}
            </span>
            <input type="checkbox" checked={on} onChange={() => toggle(val)} style={{ display: 'none' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lbl}</span>
          </label>
        );
      })}
    </div>
  );
}

/* ─── Toggle switch with label ─────────────────────────────── */
export function ToggleRow({
  value, onChange, label, desc, dark = false,
}: {
  value?: boolean;
  onChange?: (v: boolean) => void;
  label: string;
  desc?: string;
  dark?: boolean;
}) {
  const t = getThemeColors(dark);
  return (
    <label style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      border: `1px solid ${t.border}`, borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
      background: value ? (dark ? '#2a1222' : '#fdf2f8') : t.card, transition: 'background 0.12s',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: t.text }}>{label}</div>
        {desc && <div style={{ fontSize: 11.5, color: t.muted, marginTop: 2 }}>{desc}</div>}
      </div>
      <span onClick={() => onChange && onChange(!value)} style={{
        width: 40, height: 22, borderRadius: 999, flexShrink: 0, position: 'relative',
        background: value ? BRAND : t.border, transition: 'background 0.15s',
        display: 'inline-block',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: value ? 20 : 2, width: 18, height: 18,
          borderRadius: '50%', background: '#fff', transition: 'left 0.15s',
          boxShadow: '0 1px 2px rgba(16,24,40,0.2)',
        }} />
      </span>
    </label>
  );
}

/* ─── Gemeinsame Helfer der Aktions-Sections (Vertrag/Ausgleichsbetrag, PB + BK) ─── */

/** Status-Zeile unter dem Aktions-Button (Fehler rot, Erfolg grün). */
export function ActionStatus({ error, ok }: { error: string | null; ok: string | null; dark: boolean }) {
  if (!error && !ok) return null;
  return (
    <span style={{ fontSize: 12.5, fontWeight: 600, color: error ? '#b42318' : '#039855', fontFamily: 'Outfit,sans-serif' }}>
      {error ?? ok}
    </span>
  );
}

/** Read-only Historien-Tabelle (kleine, generische Ausgabe für prices/boni). */
export function HistoryTable({ dark, columns, rows }: {
  dark: boolean;
  columns: string[];
  rows: Array<Array<React.ReactNode>>;
}) {
  const t = getThemeColors(dark);
  if (rows.length === 0) return null;
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${t.borderSoft}`, borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, fontFamily: 'Outfit,sans-serif' }}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c} style={{
                textAlign: 'left', padding: '8px 12px', color: t.muted, fontWeight: 600,
                fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
                borderBottom: `1px solid ${t.borderSoft}`, background: t.cardMuted,
              }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td key={j} style={{ padding: '8px 12px', color: t.text, borderBottom: i < rows.length - 1 ? `1px solid ${t.borderSoft}` : 'none' }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** unknown → Anzeigemonat deutsch „MM.JJJJ" (z. B. „10.2026"), robust gegen ISO-Timestamps. */
export const asMonth = (v: unknown): string => {
  if (v == null) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})/);
  return m ? `${m[2]}.${m[1]}` : String(v).slice(0, 7);
};
/** unknown → €-Betrag mit 2 Nachkommastellen (oder "—"). */
export const asEuro = (v: unknown): string => {
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toFixed(2)} €` : '—';
};
